/**
 * ネイティブ環境（Capacitor で包んだ iOS / Android アプリ）の検出と、ネイティブプラグインへの橋渡し。
 *
 * WHY SDK が @capacitor/core に依存しないか:
 *   SDK は <script src="/sdk.js"> でも配る依存ゼロのファイル。Web ゲームの大半はアプリにならないので、
 *   Capacitor のコードを SDK に同梱すると全員が重くなる。Capacitor は起動時に `window.Capacitor` を置く
 *   （@capacitor/core を読み込んだアプリでは必ずある）ので、そこから**鴨の型（duck typing）**で読む。
 *   プラグインは `Capacitor.registerPlugin(name)` で取る（Capacitor 3 以降の正式な口）。無ければ旧来の
 *   `Capacitor.Plugins[name]` を見る。
 *
 * ネイティブ側の実装は packages/capacitor（@kumo/capacitor）。プラグイン名と呼び出しの形はそこの definitions.ts と一致させる。
 *   KumoSecureStorage … Keychain（iOS）/ Android Keystore で暗号化した保存
 *   KumoAuth          … ASWebAuthenticationSession / Custom Tabs、Sign in with Apple、Google Credential Manager
 *   KumoPurchases     … StoreKit 2 / Play Billing 8
 *   KumoDevice        … 触覚・外部ブラウザ・年齢レンジ（Declared Age Range / Play Age Signals）
 * 触覚は公式の @capacitor/haptics（プラグイン名 'Haptics'）があればそれも使う（device.ts）。
 */

import type { KumoStorage } from './storage.js';

export type KumoPlatform = 'web' | 'ios' | 'android';

/** window.Capacitor のうち SDK が触る部分 */
export interface CapacitorLike {
  getPlatform?(): string;
  isNativePlatform?(): boolean;
  isPluginAvailable?(name: string): boolean;
  registerPlugin?<T>(name: string): T;
  Plugins?: Record<string, unknown>;
}

/** SDK から見たネイティブ環境。テストでは偽物を KumoInitOptions.native.bridge に渡す */
export interface NativeBridge {
  platform: KumoPlatform;
  /** プラグインを取る。無ければ null（古いアプリ・プラグイン未導入）*/
  plugin<T>(name: string): T | null;
}

export function capacitorGlobal(): CapacitorLike | null {
  const c = (globalThis as { Capacitor?: CapacitorLike }).Capacitor;
  return c && typeof c === 'object' ? c : null;
}

/** 実行中のプラットフォーム。Capacitor が無い / Web ビルドの Capacitor なら 'web' */
export function detectPlatform(cap: CapacitorLike | null = capacitorGlobal()): KumoPlatform {
  if (!cap) return 'web';
  const native = typeof cap.isNativePlatform === 'function' ? cap.isNativePlatform() : false;
  if (!native) return 'web';
  const p = typeof cap.getPlatform === 'function' ? cap.getPlatform() : 'web';
  return p === 'ios' || p === 'android' ? p : 'web';
}

/** 実際の Capacitor から作る橋。Web なら null（SDK はこれまで通り Web の経路だけを使う） */
export function capacitorBridge(cap: CapacitorLike | null = capacitorGlobal()): NativeBridge | null {
  const platform = detectPlatform(cap);
  if (!cap || platform === 'web') return null;
  const cache = new Map<string, unknown>();
  return {
    platform,
    plugin<T>(name: string): T | null {
      if (cache.has(name)) return cache.get(name) as T | null;
      let p: unknown = null;
      // isPluginAvailable が false = ネイティブ側に実装が無い。registerPlugin は「呼ぶと UNIMPLEMENTED で失敗する」代理を返すので先に確かめる
      const available = typeof cap.isPluginAvailable === 'function' ? cap.isPluginAvailable(name) : true;
      if (available) {
        if (typeof cap.registerPlugin === 'function') {
          try {
            p = cap.registerPlugin<T>(name);
          } catch {
            p = null;
          }
        }
        p ??= cap.Plugins?.[name] ?? null;
      }
      cache.set(name, p);
      return p as T | null;
    }
  };
}

// ---------------------------------------------------------------- プラグインの形（@kumo/capacitor の definitions.ts と同じ）

export interface SecureStoragePlugin {
  get(o: { key: string }): Promise<{ value: string | null }>;
  set(o: { key: string; value: string }): Promise<void>;
  remove(o: { key: string }): Promise<void>;
}

/**
 * Keychain / Keystore を KumoStorage にする。キーは SDK のキー（`kumo:<pk の先頭>:refresh` 等）そのまま。
 * ネイティブ側は「このアプリ専用・この端末だけ（iCloud キーチェーンに同期しない）」で保存する（@kumo/capacitor の README）。
 */
export function secureStorage(plugin: SecureStoragePlugin): KumoStorage {
  return {
    getItem: async (key) => (await plugin.get({ key })).value ?? null,
    setItem: (key, value) => plugin.set({ key, value }),
    removeItem: (key) => plugin.remove({ key })
  };
}

/** ネイティブ環境で使う既定の保存領域。KumoSecureStorage が無ければ null（呼ぶ側は localStorage に落とす） */
export function nativeDefaultStorage(bridge: NativeBridge | null): KumoStorage | null {
  const p = bridge?.plugin<SecureStoragePlugin>('KumoSecureStorage');
  return p ? secureStorage(p) : null;
}
